import type { User } from '../modules/platform/types';
import { USER_ROLES } from './constants';
import { getEffectiveUserRoles, userHasStudentRole } from './lmsLaunch';

export const LMS_LAUNCH_SESSION_STORAGE_KEY = 'tkm_lms_launch_session';

/**
 * Launch profiles that map to the three-feature restricted LMS student sidebar.
 * Backend may still send `ai_tutor_only`; treat it as equivalent to `ai_tutor_suite`.
 */
export const LMS_RESTRICTED_NAV_LAUNCH_PROFILES = [
  'ai_tutor_only',
  'ai_tutor_suite',
] as const;

export type LmsSessionProfile = {
  isLmsLaunchSession: boolean;
  provider?: string;
  launchProfile?: string;
  createdAt?: number;
};

const DEFAULT_PROFILE: LmsSessionProfile = {
  isLmsLaunchSession: true,
  provider: 'banyanpro',
  launchProfile: 'ai_tutor_suite',
  createdAt: 0,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object';
}

export function setLmsLaunchSession(profile?: Partial<LmsSessionProfile>): void {
  try {
    const stored: LmsSessionProfile = {
      ...DEFAULT_PROFILE,
      ...profile,
      isLmsLaunchSession: true,
      createdAt: profile?.createdAt ?? Date.now(),
    };
    localStorage.setItem(LMS_LAUNCH_SESSION_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // ignore localStorage errors
  }
}

export function getLmsLaunchSession(): LmsSessionProfile | null {
  try {
    const raw = localStorage.getItem(LMS_LAUNCH_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || parsed.isLmsLaunchSession !== true) {
      return null;
    }
    return {
      isLmsLaunchSession: true,
      provider: typeof parsed.provider === 'string' ? parsed.provider : undefined,
      launchProfile: typeof parsed.launchProfile === 'string' ? parsed.launchProfile : undefined,
      createdAt: typeof parsed.createdAt === 'number' ? parsed.createdAt : undefined,
    };
  } catch {
    return null;
  }
}

export function isLmsLaunchSession(): boolean {
  return getLmsLaunchSession()?.isLmsLaunchSession === true;
}

export function clearLmsLaunchSession(): void {
  try {
    localStorage.removeItem(LMS_LAUNCH_SESSION_STORAGE_KEY);
  } catch {
    // ignore localStorage errors
  }
}

/**
 * True for LMS/SSO student sessions that should use the restricted three-item sidebar.
 * Compatible launch profiles: missing, `ai_tutor_only`, `ai_tutor_suite` (all receive restricted nav).
 */
export function isLmsStudentSession(
  user: User | null | undefined,
  isLmsLaunchSessionFlag: boolean
): boolean {
  if (!isLmsLaunchSessionFlag) return false;
  if (!user || !userHasStudentRole(user)) return false;

  const roles = getEffectiveUserRoles(user);
  if (
    roles.includes(USER_ROLES.TEACHER) ||
    roles.includes(USER_ROLES.SCHOOL_ADMIN) ||
    roles.includes(USER_ROLES.DEV_TEAM) ||
    roles.includes(USER_ROLES.PARENT)
  ) {
    return false;
  }

  return true;
}

/** Whether Layout should render sidebar chrome (wrapper + column width). */
export function computeShouldRenderSidebar(showSidebarProp: boolean | undefined): boolean {
  return showSidebarProp !== false;
}
